import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SEARCH_IMGS_NOT_LAZY_LOADED } from 'constants/appConstants';
import { HYDRA_MICROSOFT_SPONSORED_PRODUCTS } from 'constants/hydraTests';
import { evSearchPageClickthrough } from 'events/search';
import { onEvent } from 'helpers/EventHelpers';
import { Loader } from 'components/Loader';
import SearchInlineRecos from 'components/search/SearchInlineRecos';
import SearchInlineRecosNpc from 'components/search/SearchInlineRecosNpc';
import SearchSlot from 'components/search/SearchSlot';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import SearchFeedback from 'components/search/SearchFeedback';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import CrossSiteProducts from 'components/search/CrossSiteProducts';
import MicrosoftAdsWrapper from 'components/search/MicrosoftAdsWrapper';
import { MartyContext } from 'utils/context';
import ProductCard from 'components/common/ProductCard';
import InlineProductCardAd from 'components/common/InlineProductCardAd';
import { getHeartProps } from 'helpers/HeartUtils';

import css from 'styles/components/search/products.scss';

export class Products extends Component {
  static displayName = 'Products';

  state = {
    relatedStyleOpenedIndex: null,
    // Active animation index for products that get detail zoom
    animationTimerIndex: 0
  };

  componentDidMount() {
    onEvent(window, 'click', this.handleDocClick, null, this);
    onEvent(window, 'keydown', this.handleKeydownClick, null, this);
    onEvent(window, 'message', this.intentMessageHandler, null, this);
    const { products: { list } } = this.props;
    list.some(item => item.animationImages?.length) && this.startAnimationTimer(); // if we find a product that has animation images, start timer
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { products, layout, page, heartsData = {}, colorSwatchToggleState } = this.props;
    const { relatedStyleOpenedIndex, animationTimerIndex } = this.state;
    const { hearts } = heartsData;
    const { nextHeartsData = {} } = nextProps;
    const { nextHearts } = nextHeartsData;
    if (animationTimerIndex !== nextState.animationTimerIndex) {
      return true;
    }
    return nextProps.products !== products
      || nextProps.layout !== layout
      || nextProps.page !== page
      || nextHearts !== hearts
      || nextProps.colorSwatchToggleState !== colorSwatchToggleState
      || nextState.relatedStyleOpenedIndex !== relatedStyleOpenedIndex;
  }

  componentWillUnmount() {
    this.stopAnimationTimer();
  }

  // Set an initial interval ID that we can clear later when the component unmounts so we don't start two animation timers
  intervalID = 0;

  // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
  // of re-renders or lazyloading, which will tick every 800ms
  startAnimationTimer = () => {
    const MAX_ANIMATION_FRAMES = 9;
    this.intervalID = setInterval(() => {
      let newIndex = this.state.animationTimerIndex + 1;

      // Reset the cycle
      if (newIndex > MAX_ANIMATION_FRAMES) {
        newIndex = 0;
      }
      this.setState({
        animationTimerIndex: newIndex
      });
    }, 800);
  };

  stopAnimationTimer = () => {
    clearInterval(this.intervalID);
  };

  intentMessageHandler = message => {
    const msgData = message.data || {};
    const hasNamespace = msgData.namespace === 'intent' && msgData.app_name === 'decision';
    const hasCorrectFrame = msgData.event_name === 'show_source_frame' && this.microsoftAdFrame.contentWindow === message.source;
    if (hasNamespace && hasCorrectFrame) {
      this.microsoftAdFrame.classList.add(css.topProductFrame);
    }
  };

  handleDocClick = e => {
    const container = e.target.closest('[data-card-related-styles-container]');
    if (!container) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  };

  handleKeydownClick = e => {
    if (e.keyCode === 27) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  };

  setRelatedStyleOpenedIndex = index => this.setState({ relatedStyleOpenedIndex: index });

  makeCardClick = (product, index) => {
    const { onProductClicked, filters: { page }, heartsData = {}, hydraSearchColorSwatches, hydraNewProductCard } = this.props;
    const { heartsList = {} } = heartsData;
    const { productId, styleId, relatedStyles } = product;
    return (e, props, swatchIndex) => {
      const position = index + 1;
      const eventPayload = {
        pageNumber: page + 1,
        pageResultNumber: position,
        product,
        numberOfHearts: heartsList[styleId]
      };

      if (hydraSearchColorSwatches || hydraNewProductCard === 1) {
        eventPayload.swatchCount = (relatedStyles?.length || 0) + 1;
      }

      if (swatchIndex >= 0) {
        eventPayload.swatchIndex = swatchIndex;
      }

      onProductClicked(position, productId, styleId, e);
      track(() => ([
        evSearchPageClickthrough, eventPayload
      ]));
    };
  };

  makeProducts(hasMicrosoftAds) {
    const {
      productClass,
      ProductComponent,
      filters: { page, term, selected },
      products: {
        list,
        allProductsCount,
        inlineRecos,
        msftResults,
        trustedRetailers,
        recommendations
      },
      heartsData = {},
      msaImageParams,
      showRatingStars,
      colorSwatchToggleState,
      inlineBannerData,
      getProductInfo,
      triggerAssignment,
      trackMicrosoftAdImpressions,
      hydraMicrosoftSponsoredProducts,
      hydraPDPProductAngles,
      hydraCrossSiteSelling,
      hydraSearchColorSwatches,
      hydraNewProductCard,
      hydraHeartsStars,
      hydraSearchInlineEditorials,
      hydraCustAlsoBoughtThese
    } = this.props;

    const {
      onHeartClick,
      hearts,
      heartsList = {}
    } = heartsData;
    const { testId } = this.context;
    const { animationTimerIndex } = this.state;

    if (allProductsCount > 0) {

      if (msftResults.length > 0) {
        triggerAssignment(HYDRA_MICROSOFT_SPONSORED_PRODUCTS);
      }

      const combinedProducts = hydraCrossSiteSelling ? [ ...trustedRetailers, ...list] : [ ...list];

      const { isCustomer, products, heartProduct, unHeartProduct, toggleHeartingLoginModal } = this.props;
      const isDisplayCount = hydraHeartsStars === 0; // if hydra hast passes remove this line
      const heartsProps = getHeartProps({
        trackEvent,
        hearts,
        isCustomer,
        products,
        heartProduct,
        toggleHeartingLoginModal,
        unHeartProduct,
        isDisplayCount, // if hydra hast passes change this to false
        hasHearting: true
      }, {
        heartEventName: 'TE_SEARCH_PRODUCT_HEART',
        unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART'
      });

      const heartsInfo = {
        heartsData: heartsProps,
        isCustomer,
        products,
        heartProduct,
        unHeartProduct,
        toggleHeartingLoginModal
      };

      const allProductCards = hydraNewProductCard ? combinedProducts.map((v, i) => {
        const product = { ...v, index: i };
        product.msaImageParams = msaImageParams;
        product.hearts = heartsInfo;
        product.onClick = this.makeCardClick(v, i);
        return <ProductCard className={css.productCardOverrides} key={`${product.styleId}-${product.colorId}`} {...product} hydraHeartsStars={hydraHeartsStars}/>;
      }) : [];

      if (hydraSearchInlineEditorials && inlineBannerData?.placement) {
        const placement = parseInt(inlineBannerData.placement, 10);
        const adIndex = placement - 1;
        const canRenderAd = !isNaN(placement) && placement > 0 && allProductCards.length + 1 >= placement && inlineBannerData?.heading && inlineBannerData?.src && inlineBannerData?.mobilesrc;

        canRenderAd && allProductCards.splice(adIndex, 0, <InlineProductCardAd key="searchInlineHero" className={css.productCardOverrides} {...inlineBannerData} />);
      }

      const hasRecos = hydraCustAlsoBoughtThese ? recommendations?.items?.length >= 2 : inlineRecos?.recos?.length >= 2;
      return (
        <div className={cn(css.products, productClass)}>
          {!hydraNewProductCard && hydraCrossSiteSelling && <CrossSiteProducts
            trustedRetailers={trustedRetailers}
            msaImageParams={msaImageParams}
            showRatingStars={showRatingStars}
            hydraSearchColorSwatches={hydraSearchColorSwatches}
            ProductComponent={ProductComponent}
            getProductInfo={getProductInfo}/>}
          {!!hydraNewProductCard && !!hydraMicrosoftSponsoredProducts && hasMicrosoftAds && msftResults.map(({ impressionUrl, viewUrl, ...rest }, i) => {
            const product = { ...rest, index: i, isSponsored: true };
            product.msaImageParams = msaImageParams;
            product.hearts = heartsInfo;
            product.onClick = this.makeCardClick(rest, i);
            return (
              <MicrosoftAdsWrapper
                className={cn(css.productCardOverrides, css.microsoftAdsWrapper)}
                impressionUrl={impressionUrl}
                viewUrl={viewUrl}
                trackMicrosoftAdImpressions={trackMicrosoftAdImpressions}
                key={`${product.styleId}-${product.colorId}`}
              >
                {intersectionRef => (
                  <ProductCard
                    className={cn(css.productCardOverrides, css.microsoftAdsWrapper)}
                    intersectionRef={intersectionRef}
                    {...product}
                  />
                )}
              </MicrosoftAdsWrapper>
            );
          })}
          {allProductCards}
          {!hydraNewProductCard && list.map((v, i) => (
            <ProductComponent
              key={`${v.styleId}-${v.colorId}`}
              componentStyle="searchProduct"
              onComponentClick={this.makeCardClick(v, i)}
              heartsData={heartsData}
              animationTimerIndex={animationTimerIndex}
              cardData={v}
              index={i}
              msaImageParams={msaImageParams}
              melodyCardTestId={testId('searchResult')}
              showRatingStars={showRatingStars}
              shouldLazyLoad={i > SEARCH_IMGS_NOT_LAZY_LOADED}
              colorSwatchToggleState={colorSwatchToggleState}
              hydraPDPProductAngles={hydraPDPProductAngles}
              hydraSearchColorSwatches={hydraSearchColorSwatches}
              swatchClickCallback={(swatch, swatchIndex) => {
                const index = i + 1;
                track(() => ([
                  evSearchPageClickthrough, {
                    pageNumber: page + 1,
                    pageResultNumber: index,
                    product: swatch,
                    numberOfHearts: heartsList[v.styleId],
                    swatchCount: v.relatedStyles?.length,
                    swatchIndex
                  }
                ]));
              }}
              isRelatedStyleDropdownOpen={this.state.relatedStyleOpenedIndex === i}
              setRelatedStyleOpenedIndex={this.setRelatedStyleOpenedIndex}/>
          ))}
          {!hydraNewProductCard && !!hydraMicrosoftSponsoredProducts && hasMicrosoftAds ? msftResults.map(({ impressionUrl, viewUrl, ...rest }, i) => {
            const product = { ...rest, index: i, isSponsored: true };
            const additionalClasses = {
              container: css.firstRow
            };
            return (
              <MicrosoftAdsWrapper
                impressionUrl={impressionUrl}
                viewUrl={viewUrl}
                trackMicrosoftAdImpressions={trackMicrosoftAdImpressions}
                key={`${product.styleId}-${product.colorId}`}
              >
                {intersectionRef => (
                  <ProductComponent
                    additionalClasses={additionalClasses}
                    componentStyle="searchProduct"
                    onComponentClick={this.makeCardClick(product, i)}
                    heartsData={heartsData}
                    animationTimerIndex={animationTimerIndex}
                    cardData={product}
                    index={i}
                    intersectionRef={intersectionRef}
                    msaImageParams={msaImageParams}
                    melodyCardTestId={testId('searchResult')}
                    showRatingStars={showRatingStars}
                    shouldLazyLoad={i > SEARCH_IMGS_NOT_LAZY_LOADED}
                    colorSwatchToggleState={colorSwatchToggleState}
                    hydraPDPProductAngles={hydraPDPProductAngles}
                    hydraSearchColorSwatches={hydraSearchColorSwatches}
                    swatchClickCallback={(swatch, swatchIndex) => {
                      const index = i + 1;
                      track(() => ([
                        evSearchPageClickthrough, {
                          pageNumber: page + 1,
                          pageResultNumber: index,
                          product: swatch,
                          numberOfHearts: heartsList[product.styleId],
                          swatchCount: product.relatedStyles?.length,
                          swatchIndex
                        }
                      ]));
                    }}
                    isRelatedStyleDropdownOpen={this.state.relatedStyleOpenedIndex === i}
                    setRelatedStyleOpenedIndex={this.setRelatedStyleOpenedIndex}
                  />
                )}
              </MicrosoftAdsWrapper>
            );
          })
            :
            !(msftResults.length && hasMicrosoftAds && !!hydraMicrosoftSponsoredProducts) && hasRecos && (hydraNewProductCard ?
              <SearchInlineRecosNpc
                heartsInfo={heartsInfo}
                heartsData={heartsData}
                showHearting={heartsData.showFavoriteHeart}
                onHeartClick={onHeartClick}
                inlineRecos={inlineRecos}
                msaImageParams={msaImageParams}
                hearts={hearts}
                showRatingStars={showRatingStars}
                heartsList={heartsList}
                searchTerm={term}
                hydraCustAlsoBoughtThese={hydraCustAlsoBoughtThese}
                recommendations={recommendations}
                hydraHeartsStars={hydraHeartsStars}
              />
              : <SearchInlineRecos
                heartsData={heartsData}
                showHearting={heartsData.showFavoriteHeart}
                onHeartClick={onHeartClick}
                inlineRecos={inlineRecos}
                msaImageParams={msaImageParams}
                hearts={hearts}
                showRatingStars={showRatingStars}
                heartsList={heartsList}
                searchTerm={term}
              />)}
          {/* We only display the inline ad if there are enough products to have a 4th row at max products per row (5 x 3) = 15 */}
          {!hydraNewProductCard && list.length > 15 && inlineBannerData && (
            <SearchSlot
              slotDetails={inlineBannerData}
              searchTerm={term}
              selectedFilters={selected}
            />
          )}
        </div>
      );
    }
    return null;// no results view handled by NoSearchResults.js
  }

  makeSearchFeedback = () => {
    const { location, products: { isLoading }, handleSearchFeedbackClick, feedbackSubmitted } = this.props;
    const { marketplace: { search: { showSearchFeedback } } } = this.context;
    if (location?.query?.oosRedirected) { // check if this is a pdp oos redirect
      return null;
    }
    return !isLoading && showSearchFeedback && <SearchFeedback handleClick={handleSearchFeedbackClick} feedbackSubmitted={feedbackSubmitted} />;
  };

  render() {
    const { makeScrollButton, showLoader, products: { oosMessaging, isLoading }, pixelServerHost, hydraNewProductCard, hydraPills, isFacetsVisible } = this.props;
    return (
      <MartyContext.Consumer>
        {({
          testId,
          marketplace: {
            search: {
              hasMicrosoftAds
            }
          }
        }) => (
          <div id="searchPage" className={cn('searchPage', { [css.facetClass]: hydraPills && isFacetsVisible })}>
            {!hydraNewProductCard && this.makeSearchFeedback()}
            <h2>Search Results</h2>
            {oosMessaging && <p className={css.oosMessaging} data-test-id={testId('oosMessage')}>{oosMessaging}</p>}
            {hasMicrosoftAds && pixelServerHost && <iframe
              className={css.misad}
              frameBorder="0"
              title="misad"
              ref={e => this.microsoftAdFrame = e}
              src={`https://${pixelServerHost}/ads/microsoft-ads.html`} />}
            {showLoader && isLoading ? <Loader /> : this.makeProducts(hasMicrosoftAds)}
            {makeScrollButton && makeScrollButton()}
          </div>
        )}
      </MartyContext.Consumer>
    );
  }
}

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired,
  msaImageParams: PropTypes.object.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
